import { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { useSystemDate } from '../hooks/useSystemDate';
import { useWheelNavigation } from '../hooks/useWheelNavigation';

export const PayrollSummaryView = ({ handleItemSelect, selectedIndex, setSelectedIndex }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const systemDate = useSystemDate();
  const parsedDate = new Date(systemDate);

  const [selectedMonth, setSelectedMonth] = useState(parsedDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(parsedDate.getFullYear());

  const scrollRef = useRef(null);

  const payrollData = useLiveQuery(async () => {
    if (!activeCompany) return [];
    const records = await db.payroll.where('companyId').equals(activeCompany.id!).and(p => p.month === selectedMonth && p.year === selectedYear).toArray();
    
    const enriched = [];
    for (const r of records) {
      const emp = await db.employees.get(r.employeeId);
      enriched.push({
        ...r,
        employeeName: emp?.name || 'Unknown',
        empId: emp?.employeeId || 'N/A',
        department: emp?.department || 'N/A'
      });
    }
    return enriched;
  }, [activeCompany, selectedMonth, selectedYear]);

  const flatItems = payrollData || [];

  const totalNet = flatItems.reduce((sum, r) => sum + r.netSalary, 0);

  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => flatItems[selectedIndex] && handleItemSelect({ ...flatItems[selectedIndex], level: 'payslip' }));

  if (!payrollData) {
    return (
      <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-black justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585]"></div>
        <div className="mt-4 font-bold text-gray-500">Loading Payroll Register...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Salary Register</span>
        <span className="w-1/3 text-center uppercase">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-gray-100 p-2 flex items-center gap-4 border-b border-gray-300">
         <span className="font-bold">Period:</span>
         <select value={selectedMonth ?? (parsedDate.getMonth() + 1)} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="border border-gray-300 px-1 outline-none">
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</option>
            ))}
         </select>
         <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="border border-gray-300 px-1 w-[60px] outline-none" />
      </div>

      <div className="flex border-b border-gray-300 px-2 py-1 font-bold items-center text-black shadow-sm z-10 bg-gray-50">
        <div className="w-[80px]">Emp ID</div>
        <div className="flex-1">Employee Name</div>
        <div className="w-[120px]">Department</div>
        <div className="w-[80px] text-right">Basic</div>
        <div className="w-[80px] text-right">Allowances</div>
        <div className="w-[80px] text-right">Deductions</div>
        <div className="w-[100px] text-right">Net Salary</div>
        <div className="w-[80px] text-center">Action</div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-1 py-1 scrollbar-thin">
         {flatItems.length === 0 ? (
           <div className="p-8 text-center text-gray-400 italic">No payroll records found for this period.</div>
         ) : (
           flatItems.map((r: any, idx: number) => {
              const isSelected = selectedIndex === idx;
              const basic = r.basicSalary || r.earnings.basic || 0;
              const allowances = Array.isArray(r.earnings) ? r.earnings.reduce((s:number, e:any) => s + e.amount, 0) : (r.earnings.hra + r.earnings.conveyance + r.earnings.otherAllowances);
              const deductions = Array.isArray(r.deductions) ? r.deductions.reduce((s:number, d:any) => s + d.amount, 0) : (r.deductions.pf + r.deductions.esi + r.deductions.tds);
              
              return (
                 <div 
                   key={r.id} 
                   onClick={() => setSelectedIndex(idx)}
                   className={`flex px-1 py-[4px] cursor-pointer border-b border-gray-100 items-center ${isSelected ? 'bg-[#1e8585] text-white font-bold active-row' : 'text-black hover:bg-gray-50'}`}
                 >
                    <div className="w-[80px]">{r.empId}</div>
                    <div className="flex-1">{r.employeeName}</div>
                    <div className="w-[120px]">{r.department}</div>
                    <div className="w-[80px] text-right">₹ {basic.toFixed(2)}</div>
                    <div className="w-[80px] text-right">₹ {allowances.toFixed(2)}</div>
                    <div className="w-[80px] text-right text-red-500">₹ {deductions.toFixed(2)}</div>
                    <div className="w-[100px] text-right">₹ {r.netSalary.toFixed(2)}</div>
                    <div className="w-[80px] text-center">
                       <button 
                         onClick={(e) => {
                           e.stopPropagation();
                           handleItemSelect({ ...r, level: 'payslip' });
                         }}
                         className={`px-2 py-[1px] rounded text-[10px] border ${isSelected ? 'border-white text-white' : 'border-[#1e8585] text-[#1e8585]'}`}
                       >
                         Payslip
                       </button>
                    </div>
                 </div>
              );
           })
         )}
      </div>

      <div className="flex border-t border-gray-400 px-2 py-1 font-bold text-black z-10 bg-gray-50">
        <span className="flex-1">Total</span>
        <div className="w-[100px] text-right pr-20">₹ {totalNet.toFixed(2)}</div>
      </div>
    </div>
  );
};
